import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  CloseSharp as CloseIcon,
  ChevronLeft,
  ChevronRight,
  AccessTime,
  CalendarMonth,
  CheckCircleOutline,
  EventBusy,
} from "@mui/icons-material";
import {
  GetBookedAppointmentsByChatId,
  BookAppointment,
} from "../helpers/AppointmentSchedule";
import Toast from "./toast";
import { minutesToTime, timeToMinutes } from "../constants/timeUtils";
import { getAvailableSlots } from "../helpers/AppointmentSchedule";
import moment from "moment";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { minHeight } from "@mui/system";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const STEPS = [
  { label: "Select Date" },
  { label: "Pick Time" },
  { label: "Confirm" },
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

// Parses a slot string like "08:30 – 09:00" into { startMinutes, endMinutes }
function parseSlotToMinutes(slot) {
  const [startRaw, endRaw] = slot.split("–").map((s) => s.trim());
  return {
    startMinutes: timeToMinutes(startRaw),
    endMinutes: timeToMinutes(endRaw),
  };
}

const AppointmentBooking = ({
  open,
  handleClose,
  handleBookAppointmentSuccess,
  chatId,
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Separate loading states so fetching slots never masquerades as "booking"
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsFetchFailed, setSlotsFetchFailed] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  // True only once we know for sure the day has nothing open
  const noSlotsAvailable =
    !!selectedDate &&
    !isSlotsLoading &&
    !slotsFetchFailed &&
    availableSlots.length === 0;

  useEffect(() => {
    if (chatId) {
      getInitialData(chatId);
    }
  }, [open, chatId]);

  const getInitialData = async (_chatId) => {
    const response = await GetBookedAppointmentsByChatId(_chatId);
    if (
      response.statusCode === 200 &&
      response.isSuccess === true &&
      response.data
    ) {
      const data = response.data;
      setSelectedDate(new Date(data.appointmentDate));
      setSelectedSlot(
        `${minutesToTime(data.startMinute)} - ${minutesToTime(data.endMinute)}`,
      );
      setSubmitted(true);
    }
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isPast = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getFullYear() === viewYear &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getDate() === day
    );
  };

  const isToday = (day) => {
    return (
      today.getFullYear() === viewYear &&
      today.getMonth() === viewMonth &&
      today.getDate() === day
    );
  };

  const handleDayClick = async (day) => {
    if (isPast(day)) return;
    setSelectedDate(new Date(viewYear, viewMonth, day));
    setAvailableSlots([]);
    setSlotsFetchFailed(false);
    setSelectedSlot(null);
    setIsSlotsLoading(true);
    try {
      const response = await getAvailableSlots(
        moment(new Date(viewYear, viewMonth, day)).format("YYYY-MM-DD"),
      );
      if (
        response.statusCode === 200 &&
        response.isSuccess === true &&
        response.data
      ) {
        setAvailableSlots(response.data);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      setSlotsFetchFailed(true);
      setAvailableSlots([]);
    } finally {
      setIsSlotsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!selectedDate || !selectedSlot) return;

      setIsSubmitting(true);
      const { startMinutes, endMinutes } = parseSlotToMinutes(selectedSlot);
      const dayNumber = selectedDate.getDay();
      const payload = {
        chatId: chatId,
        appointmentDate: moment(selectedDate).format("YYYY-MM-DD"),
        startTime: minutesToTime(startMinutes),
        startMinute: startMinutes,
        endMinute: endMinutes,
        day: dayNumber,
      };

      const response = await BookAppointment(payload);
      setIsSubmitting(false);
      if (response.statusCode === 200 && response.isSuccess === true) {
        Toast.success("Appointment booked successfully.");
        setSubmitted(true);
      } else {
        Toast.error(
          response.message || "Failed to book appointment. Please try again.",
        );
      }
    } catch (error) {
      setIsSubmitting(false);
      Toast.error(
        error.message || "Failed to book appointment. Please try again.",
      );
    }
  };

  const handleModalClose = () => {
    if (submitted) {
      handleBookAppointmentSuccess();
    }
    setActiveStep(0);
    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setSlotsFetchFailed(false);
    setSubmitted(false);
    handleClose();
  };

  const canGoBack = () => {
    return (
      viewYear > today.getFullYear() ||
      (viewYear === today.getFullYear() && viewMonth > today.getMonth())
    );
  };

  const goNext = () => {
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  // Gate for the footer's Next / Confirm button per step
  const isNextDisabled = () => {
    if (activeStep === 0) {
      return (
        !selectedDate || isSlotsLoading || noSlotsAvailable || slotsFetchFailed
      );
    }
    if (activeStep === 1) return !selectedSlot;
    return false;
  };

  const formattedDate =
    selectedDate !== null
      ? selectedDate.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      })
      : null;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95vw", sm: "560px" },
    bgcolor: "#ffffff",
    border: "none",
    borderRadius: "16px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
    p: 0,
    minHeight: "60vh",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    outline: "none",
    overflow: "hidden",
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="appointment-modal-title"
    >
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #7B7DE5 0%, #7B7DE5 100%)",
            px: "20px",
            py: "14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarMonth sx={{ color: "#fff", fontSize: 22 }} />
            <Typography
              fontSize={17}
              fontWeight={700}
              color="#fff"
              letterSpacing={0.3}
            >
              Schedule Your Appointment - Eastern Time (New York)
            </Typography>
          </Box>
          <IconButton
            onClick={handleModalClose}
            size="small"
            aria-label="Close"
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            overflowY: "auto",
            px: "20px",
            py: "16px",
            flex: 1,
            minHeight: 0,
          }}
        >
          {submitted ? (
            /* ── Confirmation Screen ── */
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={4}
              gap={2}
            >
              <CheckCircleOutline sx={{ fontSize: 64, color: "#9293e6" }} />
              <Typography
                fontSize={20}
                fontWeight={700}
                color="#7B7DE5"
                textAlign="center"
              >
                Appointment Request Sent!
              </Typography>
              <Typography
                fontSize={14}
                color="#555"
                textAlign="center"
                maxWidth="360px"
              >
                Your medical evaluation request has been submitted. Our team
                will confirm your appointment shortly.
              </Typography>
              <Box
                sx={{
                  background: "#f0f8ff",
                  border: "1px solid #bee3f8",
                  borderRadius: "10px",
                  px: 3,
                  py: 2,
                  mt: 1,
                  width: "100%",
                  maxWidth: "340px",
                }}
              >
                <Typography
                  fontSize={13}
                  color="#7B7DE5"
                  fontWeight={600}
                  mb={0.5}
                >
                  📅 {formattedDate}
                </Typography>
                <Typography fontSize={13} color="#7B7DE5" fontWeight={600}>
                  🕐 {selectedSlot}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              {/* Step indicators */}
              <Box display="flex" alignItems="center" gap={1} mb={2.5}>
                {STEPS.map((step, idx) => (
                  <React.Fragment key={step.label}>
                    <StepBadge
                      num={idx + 1}
                      active={activeStep === idx}
                      done={activeStep > idx}
                      label={step.label}
                    />
                    {idx < STEPS.length - 1 && (
                      <Box
                        sx={{
                          flex: 1,
                          height: "1px",
                          bgcolor: activeStep > idx ? "#9293e6" : "#ddd",
                          mb: "14px",
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Box>

              {/* ── Step 1: Calendar ── */}
              {activeStep === 0 && (
                <Box
                  sx={{
                    border: "1px solid #e8edf2",
                    borderRadius: "12px",
                    overflow: "hidden",
                    mb: selectedDate ? 1.5 : 0,
                  }}
                >
                  {/* Month nav */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ px: 2, py: "10px", background: "#f7fafc" }}
                  >
                    <IconButton
                      size="small"
                      onClick={handlePrevMonth}
                      disabled={!canGoBack()}
                      aria-label="Previous month"
                      sx={{ color: canGoBack() ? "#7B7DE5" : "#ccc" }}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <Typography fontWeight={700} fontSize={15} color="#7B7DE5">
                      {MONTHS[viewMonth]} {viewYear}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleNextMonth}
                      aria-label="Next month"
                      sx={{ color: "#7B7DE5" }}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Box>

                  {/* Day labels */}
                  <Box
                    display="grid"
                    gridTemplateColumns="repeat(7, 1fr)"
                    sx={{ px: 1.5, pt: 1, pb: 0.5 }}
                  >
                    {DAYS.map((d) => (
                      <Typography
                        key={d}
                        textAlign="center"
                        fontSize={11}
                        fontWeight={700}
                        color="#94a3b8"
                        letterSpacing={0.5}
                        sx={{ textTransform: "uppercase", py: "4px" }}
                      >
                        {d}
                      </Typography>
                    ))}
                  </Box>

                  {/* Date grid */}
                  <Box
                    display="grid"
                    gridTemplateColumns="repeat(7, 1fr)"
                    sx={{ px: 1.5, pb: 1.5, gap: "2px" }}
                  >
                    {Array.from({ length: firstDay }).map((_, i) => (
                      <Box key={`empty-${i}`} />
                    ))}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const past = isPast(day);
                      const selected = isSelected(day);
                      const todayDay = isToday(day);
                      return (
                        <Box
                          key={day}
                          role="button"
                          tabIndex={past ? -1 : 0}
                          aria-disabled={past}
                          aria-pressed={selected}
                          aria-label={`${MONTHS[viewMonth]} ${day}, ${viewYear}`}
                          onClick={() => handleDayClick(day)}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && !past) {
                              e.preventDefault();
                              handleDayClick(day);
                            }
                          }}
                          sx={{
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "8px",
                            cursor: past ? "not-allowed" : "pointer",
                            fontSize: "13px",
                            fontWeight: selected ? 700 : todayDay ? 700 : 500,
                            color: past
                              ? "#ccc"
                              : selected
                                ? "#fff"
                                : todayDay
                                  ? "#9293e6"
                                  : "#334155",
                            background: selected
                              ? "linear-gradient(135deg, #7B7DE5, #9293e6)"
                              : todayDay && !selected
                                ? "#F5F5FF"
                                : "transparent",
                            border:
                              todayDay && !selected
                                ? "1px solid #9293e6"
                                : "1px solid transparent",
                            transition: "all 0.15s ease",
                            "&:focus-visible": {
                              outline: "2px solid #7B7DE5",
                              outlineOffset: "1px",
                            },
                            "&:hover": past
                              ? {}
                              : {
                                background: selected
                                  ? "linear-gradient(135deg, #7B7DE5, #9293e6)"
                                  : "#F5F5FF",
                                border: "1px solid #9293e6",
                              },
                          }}
                        >
                          {day}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* ── Step 1: Availability status for the selected date ── */}
              {activeStep === 0 && selectedDate && (
                <>
                  {isSlotsLoading ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      py={2}
                    >
                      <CircularProgress size={18} sx={{ color: "#9293e6" }} />
                      <Typography fontSize={12} color="#94a3b8">
                        Checking availability for{" "}
                        {selectedDate.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                        …
                      </Typography>
                    </Box>
                  ) : noSlotsAvailable ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      py="10px"
                      px={1.5}
                      sx={{
                        background: "#fef6f6",
                        border: "1px solid #fbd5d5",
                        borderRadius: "10px",
                      }}
                    >
                      <EventBusy sx={{ fontSize: 20, color: "#e07a7a", flexShrink: 0 }} />
                      <Typography fontSize={12.5} fontWeight={600} color="#c0392b">
                        No slots available for this date. Please select any other.
                      </Typography>
                    </Box>
                  ) : slotsFetchFailed ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      py="10px"
                      px={1.5}
                      sx={{
                        background: "#fef6f6",
                        border: "1px solid #fbd5d5",
                        borderRadius: "10px",
                      }}
                    >
                      <Typography fontSize={12.5} fontWeight={600} color="#c0392b">
                        Couldn't check availability. Please select the date again.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      py="10px"
                      px={1.5}
                      sx={{
                        background: "#F5F5FF",
                        border: "1px solid #dddeff",
                        borderRadius: "10px",
                      }}
                    >
                      <CheckCircleOutline sx={{ fontSize: 20, color: "#7B7DE5", flexShrink: 0 }} />
                      <Typography fontSize={12.5} fontWeight={600} color="#7B7DE5">
                        {availableSlots.length} slot{availableSlots.length === 1 ? "" : "s"} available for this date.
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* ── Step 2: Time Slots ── */}
              {activeStep === 1 && selectedDate && (
                <Box>
                  <Box display="flex" alignItems="center" gap={0.8} mb={1.5}>
                    <AccessTime sx={{ fontSize: 16, color: "#9293e6" }} />
                    <Typography fontSize={13} fontWeight={700} color="#7B7DE5">
                      Available slots for{" "}
                      <span style={{ color: "#9293e6" }}>
                        {selectedDate.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </Typography>
                  </Box>

                  {
                    <Box
                      display="grid"
                      gridTemplateColumns="1fr 1fr"
                      gap="8px"
                      sx={{
                        maxHeight: "260px",
                        overflowY: "auto",
                        pr: 0.5,
                        "&::-webkit-scrollbar": { width: "4px" },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#bee3f8",
                          borderRadius: "4px",
                        },
                      }}
                    >
                      {availableSlots.map((slot) => (
                        <Box
                          key={slot}
                          role="button"
                          tabIndex={0}
                          aria-pressed={selectedSlot === slot}
                          onClick={() => setSelectedSlot(slot)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedSlot(slot);
                            }
                          }}
                          sx={{
                            border:
                              selectedSlot === slot
                                ? "1.5px solid #9293e6"
                                : "1.5px solid #e2e8f0",
                            borderRadius: "8px",
                            px: 1.5,
                            py: "8px",
                            cursor: "pointer",
                            background:
                              selectedSlot === slot
                                ? "#8A8BE5"
                                : "#fff",
                            textAlign: "center",
                            transition: "all 0.15s ease",
                            "&:focus-visible": {
                              outline: "2px solid #7B7DE5",
                              outlineOffset: "1px",
                            },
                            "&:hover": {
                              border: "1.5px solid #9293e6",
                              background: "#F5F5FF",
                              color: "#8A8BE5",
                            },
                            "&:hover .MuiTypography-root": {
                              color: "#8A8BE5",
                            },
                          }}
                        >
                          <Typography
                            fontSize={12}
                            fontWeight={selectedSlot === slot ? 700 : 500}
                            color={selectedSlot === slot ? "#ffffff" : "#475569"}
                          >
                            {slot}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  }
                </Box>
              )}

              {/* ── Step 3: Confirm ── */}
              {activeStep === 2 && selectedDate && selectedSlot && (
                <Box>
                  <Typography fontSize={13} fontWeight={700} color="#7B7DE5" mb={1.5}>
                    Review your appointment
                  </Typography>
                  <Box
                    sx={{
                      background: "#F5F5FF",
                      border: "1px solid #9293e6",
                      borderRadius: "10px",
                      px: 2,
                      py: "14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "32px", width: "32px", background: "#7B7DE5", borderRadius: "8px" }}>
                        <CalendarMonthIcon sx={{ fontSize: "18px", color: "#ffffff" }} />
                      </Box>
                      <Box>
                        <Typography fontSize={12} color="#94a3b8" fontWeight={400}>
                          Date
                        </Typography>
                        <Typography fontSize={13} color="#7B7DE5" fontWeight={600}>
                      {formattedDate}
                    </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "32px", width: "32px", background: "#7B7DE5", borderRadius: "8px" }}>
                        <AccessTimeIcon sx={{ fontSize: "18px", color: "#ffffff" }} />
                      </Box>
                      <Box>
                        <Typography fontSize={12} color="#94a3b8" fontWeight={400}>
                          Time
                        </Typography>
                        <Typography fontSize={13} color="#7B7DE5" fontWeight={600}>
                          {selectedSlot}
                        </Typography>
                      </Box>
                    </Box>


                  </Box>
                  <Typography fontSize={12} color="#94a3b8" mt={1.5}>
                    Double-check the date and time above. You can go back to change either before submitting.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* ── Fixed Footer ── */}
        {!submitted ? (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: "1px solid #e8edf2",
              px: "20px",
              py: "14px",
              display: "flex",
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={activeStep === 0 ? handleModalClose : goBack}
              disabled={isSubmitting}
              sx={{
                borderColor: "#d6d9e0",
                color: "#64748b",
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 600,
                px: 3,
                "&:hover": { background: "#f7fafc", borderColor: "#9293e6" },
              }}
            >
              {activeStep === 0 ? "Cancel" : "Back"}
            </Button>

            <Box flex={1} />

            {activeStep < 2 ? (
              <Button
                variant="contained"
                onClick={goNext}
                disabled={isNextDisabled()}
                sx={{
                  background:
                    "linear-gradient(135deg, #7B7DE5 0%, #9293e6 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  px: 3.5,
                  borderRadius: "10px",
                  textTransform: "none",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #7B7DE5 0%, #a9aaff 100%)",
                  },
                  "&.Mui-disabled": {
                    background: "#e2e8f0",
                    color: "#94a3b8",
                  },
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  background:
                    "linear-gradient(135deg, #7B7DE5 0%, #9293e6 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  px: 3.5,
                  borderRadius: "10px",
                  textTransform: "none",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #7B7DE5 0%, #a9aaff 100%)",
                  },
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                    Booking…
                  </>
                ) : (
                  "Confirm Appointment"
                )}
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: "1px solid #e8edf2",
              px: "20px",
              py: "14px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleModalClose}
              sx={{
                borderColor: "#9293e6",
                color: "#9293e6",
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 600,
                px: 3,
                "&:hover": { background: "#f0f8ff", borderColor: "#7B7DE5" },
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

/* Small step badge */
const StepBadge = ({ num, active, done, label }) => (
  <Box display="flex" flexDirection="column" alignItems="center" gap={0.3}>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        background: done
          ? "linear-gradient(135deg, #7B7DE5, #9293e6)"
          : active
            ? "#F5F5FF"
            : "#f1f5f9",
        border: active || done ? "1.5px solid #9293e6" : "1.5px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        color: done ? "#fff" : active ? "#9293e6" : "#94a3b8",
        transition: "all 0.2s",
      }}
    >
      {done ? "✓" : num}
    </Box>
    <Typography
      fontSize={9}
      fontWeight={600}
      color={active || done ? "#9293e6" : "#94a3b8"}
      letterSpacing={0.3}
      sx={{ textTransform: "uppercase" }}
    >
      {label}
    </Typography>
  </Box>
);

export default AppointmentBooking;